import { createClient } from '@supabase/supabase-js';

// Essas variáveis devem ser definidas no seu arquivo .env
// Se não estiver usando .env, substitua-as pelos valores reais do seu projeto Supabase
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Sistema de monitoramento de chamadas ao Supabase
const dbLogger = {
  log: (operation: string, table: string | null, caller: string) => {
    // const timestamp = new Date().toISOString();
    // console.log(`[SUPABASE LOG - ${timestamp}] ${operation}${table ? ` - Tabela: ${table}` : ''} - Chamado por: ${caller}`);
    // Logging removed to clean up console
  }
};

// Função para obter o stack trace e encontrar o chamador
const getCaller = () => {
  const stackLines = new Error().stack?.split('\n') || [];
  // Pular as primeiras linhas (Error, getCaller, funções do proxy)
  for (let i = 3; i < stackLines.length; i++) {
    const line = stackLines[i].trim();
    // Ignorar linhas internas do Supabase
    if (line.includes('supabase') && !line.includes('/src/lib/supabase/')) continue;
    
    // Extrair o chamador
    const callerMatch = line.match(/at\s+(.*)\s+\(/);
    if (callerMatch && callerMatch[1]) {
      return callerMatch[1];
    }
    
    // Alternativa se não encontrar no formato acima
    const pathMatch = line.match(/at\s+(\/.*:\d+:\d+)/);
    if (pathMatch && pathMatch[1]) {
      // Extrair o nome do arquivo do caminho completo
      const pathParts = pathMatch[1].split('/');
      return pathParts[pathParts.length - 1].split(':')[0];
    }
  }
  
  return 'Desconhecido';
};

// Função para validar URL
const isValidUrl = (urlString: string): boolean => {
  try {
    new URL(urlString);
    return true;
  } catch (e) {
    return false;
  }
};

// Criar cliente Supabase apenas se tivermos valores válidos
let supabaseClient: any;
let initializationInProgress = false; // Flag para evitar inicializações redundantes

if (isValidUrl(supabaseUrl) && supabaseAnonKey.length > 0) {
  try {
    // Criar e exportar o cliente do Supabase
    const originalClient = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true, // persistir a sessão no localStorage
        autoRefreshToken: true, // renovar token automaticamente
        detectSessionInUrl: true, // detectar token na URL para fluxos de redefinição de senha
      },
    });
    
    // Criar um proxy para interceptar chamadas ao Supabase
    supabaseClient = new Proxy(originalClient, {
      get(target, prop, receiver) {
        const value = Reflect.get(target, prop, receiver);
        
        // Interceptar métodos específicos
        if (prop === 'from') {
          return function(table: string) {
            const fromResult = value.call(target, table);
            
            // Proxy para o resultado de .from()
            return new Proxy(fromResult, {
              get(fromTarget, fromProp, fromReceiver) {
                const fromValue = Reflect.get(fromTarget, fromProp, fromReceiver);
                
                // Verificar se o valor é uma função
                if (typeof fromValue === 'function') {
                  // Interceptar operações de banco de dados
                  if (['select', 'insert', 'update', 'delete', 'upsert'].includes(fromProp.toString())) {
                    return function(...args: any[]) {
                      const caller = getCaller();
                      dbLogger.log(`Operação: ${fromProp.toString()}`, table, caller);
                      return fromValue.apply(fromTarget, args);
                    };
                  }
                }
                
                return fromValue;
              }
            });
          };
        } else if (prop === 'auth') {
          // Proxy para auth
          return new Proxy(value, {
            get(authTarget, authProp, authReceiver) {
              const authValue = Reflect.get(authTarget, authProp, authReceiver);
              
              if (typeof authValue === 'function' && 
                  ['signIn', 'signUp', 'signOut', 'getUser', 'resetPasswordForEmail', 'updateUser'].includes(authProp.toString())) {
                return function(...args: any[]) {
                  const caller = getCaller();
                  dbLogger.log(`Auth: ${authProp.toString()}`, null, caller);
                  return authValue.apply(authTarget, args);
                };
              }
              
              return authValue;
            }
          });
        } else if (prop === 'storage') {
          // Proxy para storage
          return new Proxy(value, {
            get(storageTarget, storageProp, storageReceiver) {
              const storageValue = Reflect.get(storageTarget, storageProp, storageReceiver);
              
              if (storageProp === 'from') {
                return function(bucket: string) {
                  const bucketResult = storageValue.call(storageTarget, bucket);
                  
                  return new Proxy(bucketResult, {
                    get(bucketTarget, bucketProp, bucketReceiver) {
                      const bucketValue = Reflect.get(bucketTarget, bucketProp, bucketReceiver);
                      
                      if (typeof bucketValue === 'function' && 
                          ['upload', 'remove', 'list'].includes(bucketProp.toString())) {
                        return function(...args: any[]) {
                          const caller = getCaller();
                          dbLogger.log(`Storage: ${bucketProp.toString()}`, bucket, caller);
                          return bucketValue.apply(bucketTarget, args);
                        };
                      }
                      
                      return bucketValue;
                    }
                  });
                };
              }
              
              return storageValue;
            }
          });
        }
        
        return value;
      }
    });
    
    // Initialize library tables
    setTimeout(() => {
      if (initializationInProgress) return;
      initializationInProgress = true;
      
      import('./library').then(({ libraryService }) => {
        libraryService.initializeLibraryTables()
          .then(result => {
            if (result) {
              // Migrate data from localStorage to Supabase if needed - only once
              libraryService.migrateLocalLibrary()
                .then(() => {
                  initializationInProgress = false;
                })
                .catch(() => {
                  initializationInProgress = false;
                });
            } else {
              initializationInProgress = false;
            }
          })
          .catch(() => {
            initializationInProgress = false;
          });
      }).catch(() => {
        initializationInProgress = false;
      });
    }, 1000); // Delay to ensure app is loaded
  } catch (error) {
    // Cliente mock para evitar erros quando o Supabase não está configurado
    supabaseClient = createMockClient();
  }
} else {
  // Cliente mock para evitar erros quando o Supabase não está configurado
  supabaseClient = createMockClient();
}

// Exportar cliente
export const supabase = supabaseClient;

// Função para criar um cliente mock que não causa erros quando chamado
function createMockClient() {
  const mockResponse = { data: null, error: { message: "Supabase não está configurado corretamente" } };
  const mockSession = { data: { session: null }, error: null };
  
  return {
    from: () => ({
      select: () => Promise.resolve(mockResponse),
      insert: () => Promise.resolve(mockResponse),
      update: () => Promise.resolve(mockResponse),
      delete: () => Promise.resolve(mockResponse),
      eq: () => ({ single: () => Promise.resolve(mockResponse) }),
      order: () => Promise.resolve(mockResponse),
      limit: () => Promise.resolve(mockResponse),
      single: () => Promise.resolve(mockResponse),
      or: () => Promise.resolve(mockResponse),
      contains: () => Promise.resolve(mockResponse),
      upsert: () => Promise.resolve(mockResponse),
    }),
    auth: {
      getUser: () => Promise.resolve({ data: { user: null }, error: null }),
      getSession: () => Promise.resolve(mockSession),
      signInWithPassword: () => Promise.resolve(mockSession),
      signUp: () => Promise.resolve(mockSession),
      signOut: () => Promise.resolve({ error: null }),
      onAuthStateChange: () => ({ 
        subscription: { unsubscribe: () => {} },
        data: { subscription: { unsubscribe: () => {} } } 
      }),
      resetPasswordForEmail: () => Promise.resolve({ error: null }),
      updateUser: () => Promise.resolve({ data: null, error: null }),
    },
    storage: {
      from: () => ({
        upload: () => Promise.resolve(mockResponse),
        list: () => Promise.resolve(mockResponse),
        remove: () => Promise.resolve(mockResponse),
        getPublicUrl: () => ({ data: { publicUrl: '' } }),
      }),
    },
  };
}

// Add a function to check table structure - using a different approach
const checkTableStructure = async (tableName: string) => {
  try {
    // Em vez de tentar consultar os metadados do banco que causam erro 404,
    // vamos apenas verificar se podemos consultar a tabela
    const { data, error } = await supabaseClient
      .from(tableName)
      .select('id')
      .limit(1);
      
    if (error) {
      return false;
    }
    
    return true;
  } catch (e) {
    return false;
  }
};

// Simplificar a verificação do schema do banco de dados
const checkDatabaseSchema = async () => {
  try {
    // Verificar apenas se as tabelas existem
    const libraryItemsExists = await checkTableStructure('library_items');
    const libraryCollectionsExists = await checkTableStructure('library_collections');
    
    // Verificar o acesso aos dados através de queries básicas
    if (libraryItemsExists) {
      await supabaseClient
        .from('library_items')
        .select('id')
        .limit(1);
    }
    
    if (libraryCollectionsExists) {
      await supabaseClient
        .from('library_collections')
        .select('id')
        .limit(1);
    }
  } catch (error) {
    // Erro silenciado
  }
};

// Modificar a inicialização para evitar requisições desnecessárias
// Executar apenas uma verificação simples de schema, sem duplicações
let schemaCheckComplete = false;
let lastSchemaCheck = 0;
const SCHEMA_CHECK_INTERVAL = 86400000; // 24 horas em milissegundos

setTimeout(() => {
  const now = Date.now();
  // Verificar se já foi feito check nas últimas 24h
  const lastCheckStr = localStorage.getItem('voah_schema_last_check');
  if (lastCheckStr) {
    lastSchemaCheck = parseInt(lastCheckStr, 10);
  }

  if (!schemaCheckComplete && (now - lastSchemaCheck > SCHEMA_CHECK_INTERVAL)) {
    schemaCheckComplete = true;
    checkDatabaseSchema().then(() => {
      // Registrar timestamp do último check
      localStorage.setItem('voah_schema_last_check', now.toString());
    });
  }
}, 2000); 